/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * COSE algorithm identifiers (RFC 8152 §8) → WebCrypto import/verify parameters
 * and the c2pa-rs `SigningAlg` label.
 */

import type { SigningAlg } from '../types'

export interface AlgSpec {
    signingAlg: SigningAlg
    /** WebCrypto importKey algorithm. */
    importParams: RsaHashedImportParams | EcKeyImportParams | Algorithm
    /** WebCrypto verify algorithm. */
    verifyParams: RsaPssParams | EcdsaParams | Algorithm
}

const SHA = (n: 256 | 384 | 512): string => `SHA-${n}`

/** SPKI algorithm OIDs and EC-curve OIDs, for binding a COSE `alg` to the key type. */
const OID_RSA = '1.2.840.113549.1.1.1'
const OID_RSASSA_PSS = '1.2.840.113549.1.1.10'
const OID_EC_PUBLICKEY = '1.2.840.10045.2.1'
const OID_ED25519 = '1.3.101.112'
const EC_CURVE_OID: Record<string, string> = {
    'P-256': '1.2.840.10045.3.1.7',
    'P-384': '1.3.132.0.34',
    'P-521': '1.3.132.0.35'
}

/**
 * §13.2.1 (l.2176): a validator MUST refuse to verify a signature with a key that
 * is not correct for the algorithm choice (COSE-1). Binds the protected-header
 * `alg` to the leaf certificate's actual key type explicitly, rather than leaning
 * on WebCrypto's `importKey`/`verify` to throw on a mismatch.
 *   • PS256/384/512 ⇒ an RSA or RSASSA-PSS key
 *   • ES256/384/512 ⇒ an ecPublicKey on the matching curve
 *   • Ed25519       ⇒ an Ed25519 key
 */
export function coseAlgMatchesKeyType (coseAlg: number, spkiAlgOid: string, ecCurveOid?: string): boolean {
    const spec = algSpec(coseAlg)
    if (!spec) return false
    const importName = (spec.importParams as { name: string }).name
    if (importName === 'RSA-PSS') return spkiAlgOid === OID_RSA || spkiAlgOid === OID_RSASSA_PSS
    if (importName === 'ECDSA') {
        const wantCurve = EC_CURVE_OID[(spec.importParams as EcKeyImportParams).namedCurve]
        return spkiAlgOid === OID_EC_PUBLICKEY && ecCurveOid === wantCurve
    }
    if (importName === 'Ed25519') return spkiAlgOid === OID_ED25519
    return false
}

export function algSpec (coseAlg: number): AlgSpec | null {
    switch (coseAlg) {
        // RSASSA-PSS
        case -37: return { signingAlg: 'Ps256', importParams: { name: 'RSA-PSS', hash: SHA(256) }, verifyParams: { name: 'RSA-PSS', saltLength: 32 } }
        case -38: return { signingAlg: 'Ps384', importParams: { name: 'RSA-PSS', hash: SHA(384) }, verifyParams: { name: 'RSA-PSS', saltLength: 48 } }
        case -39: return { signingAlg: 'Ps512', importParams: { name: 'RSA-PSS', hash: SHA(512) }, verifyParams: { name: 'RSA-PSS', saltLength: 64 } }
        // ECDSA (COSE signatures are raw r||s, which WebCrypto ECDSA expects)
        case -7: return { signingAlg: 'Es256', importParams: { name: 'ECDSA', namedCurve: 'P-256' }, verifyParams: { name: 'ECDSA', hash: SHA(256) } }
        case -35: return { signingAlg: 'Es384', importParams: { name: 'ECDSA', namedCurve: 'P-384' }, verifyParams: { name: 'ECDSA', hash: SHA(384) } }
        case -36: return { signingAlg: 'Es512', importParams: { name: 'ECDSA', namedCurve: 'P-521' }, verifyParams: { name: 'ECDSA', hash: SHA(512) } }
        // EdDSA (Ed25519)
        case -8: return { signingAlg: 'Ed25519', importParams: { name: 'Ed25519' }, verifyParams: { name: 'Ed25519' } }
        default: return null
    }
}
